<template>
  <div
    class="flex h-8 flex-row rounded-3xl border"
    :class="{
      'border-indigo-450': highlight || locateSelfHighlight,
      'border-primary-200': !(highlight || locateSelfHighlight),
      'w-full': stretch,
    }"
  >
    <button
      class="flex items-center gap-x-1 rounded-l-3xl px-2 text-white 2xs:pr-4"
      :class="{ 'bg-indigo-700': locateSelfHighlight, 'bg-blue-400': !locateSelfHighlight }"
      @click="findMe()"
    >
      <gv-icon name="near_me" size="xs" :fill="locateSelfHighlight" class="fill-white" />
      <div v-if="!isMobile" class="whitespace-nowrap text-xs text-white">Find me</div>
    </button>

    <gv-dropdown class="flex w-full flex-row">
      <template #default="{ setOpen, setClosed }">
        <div class="flex w-full flex-row items-center rounded-r-3xl" :class="{ 'bg-indigo-50': highlight }">
          <gv-icon-sharp name="search" aria-hidden="true" size="xs" class="mx-2 min-w-4 fill-grey-300 p-0" />
          <input
            v-model="searchTerm"
            class="w-full border-0 bg-transparent p-0 focus:outline-none focus:ring-0"
            :placeholder="isMobile ? 'Search' : 'Search maps'"
            type="text"
            aria-label="Search maps"
            @focus="onFocus()"
            @blur="onUnFocus()"
            @input="searchTerm.length >= 5 ? setOpen() : closeMenu(setClosed)"
            @keydown.enter="goToCoordinates() && closeMenu(setClosed)"
          />

          <button
            v-if="searchTerm"
            class="mr-2"
            title="Clear search"
            aria-label="Clear search"
            @click.stop="
              () => {
                onSearchClear();
                closeMenu(setClosed);
              }
            "
          >
            <gv-icon name="cancel_fill" size="xs" fill aria-hidden="true" />
          </button>
          <div v-else class="mr-2 size-4" />
        </div>
      </template>
      <template #menu="{ setClosed }">
        <gv-menu ref="menuOptions" class="max-h-80 overflow-auto shadow-lg" size="full">
          <gv-menu-section>
            <gv-menu-item
              v-if="menuLatLngOption"
              key="latlng"
              class="px-4"
              icon="location_on"
              :text="`${menuLatLngOption[0]}, ${menuLatLngOption[1]}`"
              @click="
                goToCoordinates();
                closeMenu(setClosed);
              "
            >
              <div class="flex items-center gap-x-2">
                <gv-icon name="location_on" size="xs" class="fill-primary" />

                <gv-type wrap="nowrap" scheme="custom" :text="`${menuLatLngOption[0]}, ${menuLatLngOption[1]}`" />
              </div>
            </gv-menu-item>
            <gv-menu-item
              v-if="menuLatLngOption"
              key="latlng"
              class="px-4"
              icon="location_on"
              :text="`${menuLatLngOption[0]}, ${menuLatLngOption[1]}`"
              @click="
                goToCoordinates();
                closeMenu(setClosed);
              "
            >
              <div class="flex items-center gap-x-2">
                <gv-icon name="location_on" size="xs" class="fill-primary" />

                <gv-type wrap="nowrap" scheme="custom" :text="`${menuLatLngOption[0]}, ${menuLatLngOption[1]}`" />
              </div>
            </gv-menu-item>
            <gv-menu-item
              v-for="(feature, i) in geocode?.geocoding?.features"
              :key="i"
              class="px-4"
              icon="location_on"
              :text="`${feature.properties.name}, ${feature.properties.place_formatted}`"
              @click="
                onItemSelected(feature);
                closeMenu(setClosed);
              "
            >
              <div class="flex items-center gap-x-2">
                <gv-icon name="location_on" size="xs" class="fill-primary" />

                <gv-type wrap="nowrap" scheme="custom" :text="formatAddress(feature, ' ')" />
              </div>
            </gv-menu-item>
          </gv-menu-section>
        </gv-menu>
      </template>
    </gv-dropdown>
  </div>
</template>

<script setup lang="ts">
import { BREAKPOINTS } from '@/constants/index';
import { formatAddress, GeocodingResponseFeatures, HazardView, useGeocodeStore, useHazardsStore, useMapStore } from '@/features/hazards';
import { throttledWatch, useBreakpoints } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { inject, ref } from 'vue';
import * as Symbols from '@/common/constants/symbols';
import { captureException } from '@sentry/vue';

const hazardsStore = useHazardsStore();
const mapStore = useMapStore();
const { addOrSetSource, flyToPosition, flyToCurrentPosition } = mapStore;
const { geolocate } = storeToRefs(mapStore);

const { searchTerm, activeView } = storeToRefs(hazardsStore);

const geocode = useGeocodeStore();

const breakpoints = useBreakpoints(BREAKPOINTS);
const isMobile = breakpoints.smaller('2xs');

const menuOptions = ref();
const menuLatLngOption = ref<[string, string] | undefined>(undefined);

const lngLatPairPattern = /^\s*(?<lat>-?\d+(?:\.\d+)?),\s*(?<lng>-?\d+(?:\.\d+)?)\s*$/;

const injected = {
  enableGeolocateBottomSheet: inject(Symbols.EnableGeolocateBottomSheet),
};

defineProps<{
  stretch?: boolean;
}>();

const emit = defineEmits<{
  (event: 'text-input:unfocused'): void;
  (event: 'text-input:focused'): void;
}>();

const locateSelfHighlight = ref<boolean>(false);
const highlight = ref<boolean>(false);

throttledWatch(
  searchTerm,
  async () => {
    if (searchTerm.value.length >= 5) {
      await geocode.getForwardGeocoding(searchTerm.value);

      // Check if input is a lat/lng pair
      const { lat, lng } = lngLatPairPattern.exec(searchTerm.value)?.groups ?? {};
      if (lat && lng && !Number.isNaN(lat) && !Number.isNaN(lng)) {
        menuLatLngOption.value = [lat, lng];
      } else {
        menuLatLngOption.value = undefined;
      }

      menuOptions.value.menuDiv.scrollTo(0, 0);
    }
  },
  { throttle: 500 }
);

const findMe = () => {
  locateSelfHighlight.value = !locateSelfHighlight.value;
  if (locateSelfHighlight.value) flyToCurrentPosition();

  // This will be triggered if there is an error in geolocation
  // e.g. user denied location access
  geolocate.value?.once('error', (error) => {
    // eslint-disable-next-line no-console
    captureException(error);
    injected.enableGeolocateBottomSheet?.setOpen();
    locateSelfHighlight.value = false;
  });
};

/**
 * Tries to go directly to coordinates specified in the searchTerm
 * @returns boolean indicating if the search term was valid coordinates and that the flyToPosition worked.
 */
const goToCoordinates = (): boolean => {
  if (!searchTerm.value) return false;

  const { lat, lng } = lngLatPairPattern.exec(searchTerm.value)?.groups ?? {};
  if (!lat || !lng || Number.isNaN(lat) || Number.isNaN(lng)) return false;

  // Mapbox expects [longitude, latitude]
  const coordinates: [number, number] = [+lng, +lat];
  flyToPosition({ center: coordinates, zoom: 13 });
  addOrSetSource('address-pin-source', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Point',
            coordinates,
          },
        },
      ],
    },
  });

  return true;
};

const onItemSelected = (features: GeocodingResponseFeatures) => {
  if (activeView.value === HazardView.List) {
    hazardsStore.setActiveView(HazardView.Map);
  }

  searchTerm.value = formatAddress(features, ' ');
  const coordinates: [number, number] = [features.properties.coordinates.longitude, features.properties.coordinates.latitude];
  flyToPosition({ center: coordinates, zoom: 13 });

  addOrSetSource('address-pin-source', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Point',
            coordinates,
          },
        },
      ],
    },
  });
};

const onSearchClear = () => {
  addOrSetSource('address-pin-source', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
  searchTerm.value = '';
  onUnFocus();
};

const closeMenu = (setClosed: () => void) => {
  geocode.clearGeocoding();
  setClosed();
};

const onUnFocus = () => {
  highlight.value = false;
  if (searchTerm.value === '') {
    emit('text-input:unfocused');
  }
};

const onFocus = () => {
  highlight.value = true;
  emit('text-input:focused');
};
</script>

<style scoped>
::placeholder {
  color: var(--color-type-secondary) !important;
}
</style>
